import { useContext, useMemo } from "react";
import { Button, Image, Tooltip } from "antd";
import {
    DeleteOutlined,
    FilePdfOutlined,
} from "@ant-design/icons";
import { IoCheckmark, IoCheckmarkDone } from "react-icons/io5";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import { IMessageContact } from "../../interfaces";
import { formatDateStringToDateAndTime } from "../../../../utils/Conversores";
import { urlHttpGov } from "../../../../services/urls";

interface IProps {
    message: IMessageContact;
    isInitial?: boolean;
    handleOpenFile: (file: string) => void;
    handleDeleteMessage: (id: number) => void;
}

export default function CardMessageContact({
    message,
    isInitial = false,
    handleOpenFile,
    handleDeleteMessage,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const isOperator = Boolean(
        message.panel_user?.id ||
        (message.panel_user_id !== null && message.panel_user_id !== undefined)
    );
    const senderName = isOperator
        ? message.panel_user?.name || "Suporte"
        : message.app_user?.name || "Cidadão";

    const formattedViewedAt = useMemo(() => {
        if (!message.viewed_at) return null;
        try {
            const date = new Date(message.viewed_at);
            if (isNaN(date.getTime())) return null;
            return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
        } catch {
            return null;
        }
    }, [message.viewed_at]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: isOperator ? "flex-end" : "flex-start",
                marginBottom: 12,
                width: "100%",
            }}
        >
            <div
                style={{
                    maxWidth: "85%",
                    minWidth: 160,
                    background: isOperator
                        ? isDark
                            ? "#005c4b"
                            : "#d9fdd3"
                        : isDark
                            ? "#202c33"
                            : "#ffffff",
                    border: isOperator
                        ? `1px solid ${isDark ? "#007a64" : "#b7eb8f"}`
                        : `1px solid ${isDark ? "#2a3942" : "#e2e8f0"}`,
                    borderRadius: isOperator
                        ? "14px 14px 2px 14px"
                        : "14px 14px 14px 2px",
                    padding: "10px 14px",
                    boxShadow: isDark
                        ? "0 1px 2px rgba(0,0,0,0.3)"
                        : "0 1px 3px rgba(0,0,0,0.06)",
                    position: "relative",
                }}
            >
                {/* Header: Sender Name & Optional Delete */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        marginBottom: 4,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span
                            style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                color: isOperator
                                    ? isDark
                                        ? "#53bdeb"
                                        : "#008069"
                                    : isDark
                                        ? "#53bdeb"
                                        : "#1677ff",
                            }}
                        >
                            {isOperator ? `${senderName} (Suporte)` : senderName}
                        </span>
                    </div>

                    {isOperator && (
                        <Tooltip title="Excluir mensagem enviada">
                            <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined style={{ fontSize: 12, color: "#ff4d4f" }} />}
                                onClick={() => handleDeleteMessage(message.id)}
                                style={{
                                    height: 20,
                                    width: 20,
                                    padding: 0,
                                    lineHeight: "20px",
                                    opacity: 0.75,
                                }}
                            />
                        </Tooltip>
                    )}
                </div>

                {/* Message Text Content */}
                {message.message && (
                    <div
                        style={{
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: isDark ? "#e9edef" : "#111b21",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            marginBottom: message.files && message.files.length > 0 ? 8 : 4,
                        }}
                    >
                        {message.message}
                    </div>
                )}

                {/* Attachments Section */}
                {message.files && message.files.length > 0 && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                            marginTop: 4,
                            marginBottom: 4,
                        }}
                    >
                        {message.files.map((fileObj, idx) => {
                            const fileName = fileObj.file;
                            const isImage =
                                fileName.toLowerCase().endsWith(".png") ||
                                fileName.toLowerCase().endsWith(".jpg") ||
                                fileName.toLowerCase().endsWith(".jpeg") ||
                                fileName.toLowerCase().endsWith(".webp");

                            const fileUrl = `${urlHttpGov}/contact/${fileName}`;

                            if (isImage) {
                                return (
                                    <div
                                        key={idx}
                                        style={{
                                            borderRadius: 8,
                                            overflow: "hidden",
                                            maxWidth: 220,
                                            border: `1px solid ${isDark ? "#333" : "#d9d9d9"}`,
                                        }}
                                    >
                                        <Image
                                            src={fileUrl}
                                            alt={`Anexo ${idx + 1}`}
                                            style={{
                                                width: "100%",
                                                maxHeight: 160,
                                                objectFit: "cover",
                                                display: "block",
                                            }}
                                        />
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={idx}
                                    onClick={() => handleOpenFile(fileName)}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "6px 10px",
                                        background: isDark ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.7)",
                                        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        maxWidth: 260,
                                        fontSize: 12,
                                        color: isDark ? "#e9edef" : "#111b21",
                                    }}
                                >
                                    <FilePdfOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
                                    <span
                                        style={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {fileName}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Footer Timestamp & Read Confirmation */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 5,
                        marginTop: 4,
                    }}
                >
                    <span
                        style={{
                            fontSize: 10.5,
                            color: isDark ? "#8696a0" : "#667781",
                        }}
                    >
                        {message.created_at ? formatDateStringToDateAndTime(message.created_at) : ""}
                    </span>

                    {/* Indicador de leitura exibido apenas para mensagens enviadas pelo suporte */}
                    {isOperator && (
                        <Tooltip
                            title={
                                message.viewed
                                    ? formattedViewedAt
                                        ? `Visualizado pelo cidadão em ${formattedViewedAt}`
                                        : "Visualizado pelo cidadão"
                                    : "Enviado ao usuário (não visualizado)"
                            }
                        >
                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 3,
                                    cursor: "help",
                                    color: message.viewed
                                        ? (isDark ? "#53bdeb" : "#0284c7")
                                        : (isDark ? "#8696a0" : "#94a3b8"),
                                }}
                            >
                                {message.viewed ? (
                                    <>
                                        <IoCheckmarkDone style={{ fontSize: 14 }} />
                                        <span style={{ fontSize: 10, fontWeight: 500 }}>Visto</span>
                                    </>
                                ) : (
                                    <>
                                        <IoCheckmark style={{ fontSize: 13 }} />
                                        <span style={{ fontSize: 10, fontWeight: 500 }}>Enviado</span>
                                    </>
                                )}
                            </span>
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
}